/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";

import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";

import { Alert, CircularProgress, Link, Stack } from "@mui/material";

import { LoginFunction } from "../../utils/Interfaces";
import ChangePasswordRequestModal from "../reset-password/ChangePasswordRequestModal";
import Grid from "@mui/material/Grid";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

export interface LoginProps {
    onLogin: LoginFunction;
}

const Login = (props: LoginProps) => {
    const { onLogin } = props;

    const [acctInfo, setAcctInfo] = useState({ username: "", password: "" });

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [errorMessage, setErrorMessage] = useState<string>("");
    const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState<boolean>(false);

    const [loggingIn, setLoggingIn] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAcctInfo({
            ...acctInfo,
            [e.target.name]: e.target.value,
        });
    };

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />

            <ChangePasswordRequestModal handleClose={() => setForgotPasswordModalOpen(false)} open={forgotPasswordModalOpen} />

            <Box
                sx={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                    <LockOutlinedIcon />
                </Avatar>
                <Typography component="h1" variant="h5" marginBottom={"10px"}>
                    Sign in
                </Typography>
                {errorMessage.length > 0 && (
                    // @ts-ignore
                    <Alert severity={"error"} marginTop={20}>
                        {errorMessage}
                    </Alert>
                )}
                <Box
                    component="form"
                    onSubmit={(event) => {
                        setLoggingIn(true);

                        event.preventDefault();
                        onLogin(
                            acctInfo,
                            () => {
                                setLoggingIn(false);
                                setErrorMessage("");
                            },
                            () => {
                                setLoggingIn(false);
                                setErrorMessage("Username of password is incorrect.");
                            },
                            dispatch
                        );
                    }}
                    noValidate
                    sx={{ mt: 1 }}
                >
                    <TextField
                        onChange={handleChange}
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label="Username"
                        name="username"
                        autoComplete="username"
                        autoFocus
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        onChange={handleChange}
                    />
                    {!loggingIn && (
                        <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                            Sign In
                        </Button>
                    )}
                    {loggingIn && (
                        <Stack direction="row" justifyContent="center" sx={{ mt: 3, mb: 4 }}>
                            <CircularProgress size={20} />
                        </Stack>
                    )}
                    <Grid container>
                        <Grid item xs>
                            <Link
                                onClick={() => setForgotPasswordModalOpen(true)}
                                variant="body2"
                                sx={{
                                    cursor: "pointer",
                                }}
                            >
                                Forgot password?
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Container>
    );
};

export default Login;
